import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { decodeJwt } from "@/lib/decode-jwt";
import { USER_ORIGIN_WEBAPP_LEGACY } from "@/lib/user-origin";
import {
  apiFetch,
  refreshAccessToken,
  setAccessToken,
  setAuthExpiredHandler,
  subscribeAccessToken,
} from "@/lib/api-client";
import {
  clearDeviceSignedOut,
  clearOfflineIdentity,
  hasPendingOfflineWork,
  isDeviceSignedOut,
  isDeviceSignedOutLatch,
  isWithinGrace,
  markDeviceSignedOut,
  persistOfflineIdentity,
  readOfflineIdentity,
  subscribeDeviceSignedOut,
} from "@/lib/offline-session";
import { forgetCachedOwnerIdentity, persistOwnerIdentity } from "@/lib/owner-scope";
import { triggerMainThreadDrain } from "@/lib/drain-runner";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export type Role = "super_admin" | "org_admin" | "clinician";

export interface AuthUser {
  userId: string;
  email: string;
  role: Role | string;
  orgId: string | null;
  orgRole: string | null;
  forcePasswordChange: boolean;
  /**
   * Story 16.2 — account provenance off the JWT; null for pre-16.2 tokens.
   * Optional so the many non-legacy test fixtures need no edit (absent ⇒
   * non-legacy); `toUser` always populates it at runtime.
   */
  userOrigin?: string | null;
  /** Story 16.2 — super_admin cert verdict; false until approved (16.4). Optional, as above. */
  certVerified?: boolean;
  /**
   * Franklin#445 — account-enabled flag (FR39) off the JWT. The legacy gate
   * reads THIS (not certVerified): an approved-but-unpaid legacy clinician is
   * cert_verified=true yet is_active=false and must stay gated until Story 16.7
   * (Stripe). Optional so non-legacy test fixtures need no edit; `toUser` always
   * populates it at runtime.
   */
  isActive?: boolean;
  /**
   * Story 8.1 (FR38) — the legacy-import calibration bypass off the JWT. True ⇒
   * the CalibrationGate passes synchronously (zero status queries, offline-safe);
   * false/absent ⇒ the gate consults GET /calibration/status. Fail-SAFE either
   * way: an absent claim only costs a query — the backend CALIB-001 gate is the
   * enforcement authority. Optional so pre-8.1 test fixtures need no edit.
   */
  alreadyCertified?: boolean;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  accessToken: string | null;
  /** Adopt an access token (e.g. after the Story 2.10 login chain returns one). */
  setSession: (accessToken: string) => void | Promise<void>;
  /** Revoke the refresh family server-side, then clear local state. */
  signOut: () => Promise<void>;
  /** True when the current user holds one of the given roles. */
  hasRole: (...roles: Array<Role | string>) => boolean;
  /**
   * Story 16.2 / Franklin#445 — true when the user is a legacy clinician whose
   * account is not yet enabled (`user_origin === "webapp_legacy" && !is_active`).
   * Drives the gated app-shell (locked nav, pending pill, warn banner) and the
   * route guard that pins them to Profile. Absent-safe → false for everyone else.
   * Required (not optional): a security-relevant flag must never read `undefined`,
   * which is falsy and would silently fail OPEN in the guards. The provider always
   * sets a concrete boolean and every test mock derives it via `deriveIsLegacyGated`.
   */
  isLegacyGated: boolean;
}

// Exported so tests can supply a controlled value without standing up the provider.
export const AuthContext = React.createContext<AuthContextValue | null>(null);

function toUser(token: string): AuthUser | null {
  const claims = decodeJwt(token);
  // Fail closed: a token missing the identity claims — or carrying non-string values
  // for them — is treated as no session rather than yielding a half-formed user.
  if (
    !claims ||
    typeof claims.sub !== "string" ||
    typeof claims.email !== "string" ||
    typeof claims.role !== "string"
  ) {
    return null;
  }
  return {
    userId: claims.sub,
    email: claims.email,
    role: claims.role,
    orgId: claims.org_id ?? null,
    orgRole: claims.org_role ?? null,
    forcePasswordChange: Boolean(claims.force_password_change),
    userOrigin: claims.user_origin ?? null,
    certVerified: Boolean(claims.cert_verified),
    isActive: Boolean(claims.is_active),
    alreadyCertified: Boolean(claims.already_certified),
  };
}

/**
 * Story 16.2 / Franklin#445 — a legacy clinician is "gated" while their account
 * is disabled-by-default: signed up through the web app
 * (`user_origin === "webapp_legacy"`) and NOT yet account-enabled (`is_active`).
 * Centralised so the shell, guard, and tests derive it one way.
 *
 * The gate keys off `is_active`, NOT `cert_verified` (the #445 fix): cert
 * approval flips `cert_verified=true` but leaves `is_active=false` (the FR39
 * Stripe gate stays closed until Story 16.7), so a verified-but-unpaid clinician
 * MUST stay gated. This mirrors the backend's own `get_active_user` authority.
 * The gate auto-releases on the next token rotation once 16.7 flips
 * `is_active=true` — no further FE change.
 *
 * Fail-CLOSED (`isActive !== true`): a legacy token lacking an explicit
 * `is_active=true` (pre-#445 token, malformed claim) reads as gated rather than
 * silently fail-open. Absent-safe: any non-legacy user is never gated.
 */
export function deriveIsLegacyGated(user: AuthUser | null): boolean {
  return user?.userOrigin === USER_ORIGIN_WEBAPP_LEGACY && user.isActive !== true;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // AuthProvider is mounted inside QueryClientProvider (App.tsx), so this resolves the
  // single app-wide client — the one EVERY logout path must wipe to prevent cross-session
  // PHI bleed (wiped at the applyToken(null) chokepoint below).
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<AuthStatus>("loading");
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [accessToken, setToken] = React.useState<string | null>(null);

  // The userId currently reflected in the query cache. Tracked in a ref (not `user` state) so
  // `applyToken` can compare it without re-subscribing on every user change — and so the compare
  // is correct even against a userId set by an OFFLINE restore, which never goes through a token.
  // gh-461: offline-restore is the first way to become authenticated WITHOUT a preceding
  // applyToken(null) cache-clear in a page load, so a later cross-user login must wipe the cache
  // itself (preserves gh-365/#368).
  const appliedUserIdRef = React.useRef<string | null>(null);

  // Single place that maps an in-memory token to React state. Used both by the
  // boot/refresh flow and by background refreshes (via the token subscription).
  const applyToken = React.useCallback(
    (token: string | null) => {
      // Franklin#867 Part A — refuse silent-refresh resurrection after an explicit sign-out.
      // `setSession` clears the latch BEFORE calling setAccessToken, so interactive login is fine;
      // a racing refresh that still holds a live cookie must not promote. Clearing the module token
      // re-enters here as `null` via the subscription.
      if (token && isDeviceSignedOutLatch()) {
        setAccessToken(null);
        return;
      }
      const nextUser = token ? toUser(token) : null;
      if (token && nextUser) {
        // Cross-user defense (gh-461 / gh-365 / #368): if the incoming session belongs to a
        // DIFFERENT clinician than the one currently cached, wipe the cache BEFORE promoting so the
        // new clinician can never be served the prior one's PHI. Same user (e.g. an offline-restored
        // session promoting on reconnect) does NOT clear — that would needlessly drop live data.
        if (appliedUserIdRef.current && appliedUserIdRef.current !== nextUser.userId) {
          queryClient.clear();
        }
        appliedUserIdRef.current = nextUser.userId;
        setToken(token);
        setUser(nextUser);
        setStatus("authenticated");
        // Stamp the credential-free offline identity at the single network-validated chokepoint
        // (login/forced-change setSession + every successful silent refresh). Best-effort.
        void persistOfflineIdentity(nextUser, Date.now());
        // Story 19.6 Unit E — stamp the DEVICE identity too, as a SEPARATE durable fact. Two calls,
        // not one, because their lifetimes differ and that difference is the point (AC22): the blob
        // above is deleted on sign-out and on every expiry path, while this key is never deleted —
        // the local drafts it scopes deliberately survive both. It is what lets the offline reads on
        // a shared device tell whose rows they are looking at.
        void persistOwnerIdentity(nextUser.userId);
      } else {
        // Single chokepoint for EVERY transition to unauthenticated — explicit sign-out,
        // session expiry, and a failed background refresh all funnel through here
        // (signOut → setAccessToken(null) → subscription; expiry → refreshAccessToken /
        // onAuthExpired → setAccessToken(null) → subscription). Wipe ALL cached server
        // state BEFORE the unauthenticated re-render so the next session in this same tab
        // can never be served the prior clinician's cached PHI (Franklin#365 + #368
        // review: covers expiry→relogin, not just signOut). clear() removes the data
        // outright; invalidateQueries() would keep stale rows for a background-refetch flash.
        //
        // gh-461: this path deliberately does NOT clear the persisted `auth_session` identity —
        // that clear is REASON-GATED at the callers (genuine `!res.ok` expiry / explicit sign-out)
        // so a transient offline blip (which no longer reaches here after the refresh split) can
        // never wipe the identity and re-break the offline-reload restore.
        queryClient.clear();
        appliedUserIdRef.current = null;
        // Story 19.6 Unit E — drop the IN-MEMORY owner mirror only, so "no session ⇒ no owner in
        // memory" holds for this page load. The DURABLE `meta.owner_identity` key is deliberately
        // left in place (like the drafts it scopes): deleting it would orphan every local row the
        // next release could still attribute, for no gain.
        forgetCachedOwnerIdentity();
        setToken(null);
        setUser(null);
        setStatus("unauthenticated");
      }
    },
    [queryClient],
  );

  // gh-461 — restore a credential-free, offline-LIMITED session from the persisted non-secret
  // identity: authenticated, but `accessToken:null` and NO cache clear (the same clinician is
  // resuming their own unsynced work). Reveal + Submit stay network-gated (they throw NETWORK_ERROR
  // offline). The drain kick below is token-gated to a no-op on this path (gh-634 — the token is
  // always null here); replay waits for the reconnect promotion, itself a drain trigger.
  const restoreOfflineIdentity = React.useCallback((offlineUser: AuthUser) => {
    appliedUserIdRef.current = offlineUser.userId;
    // Story 19.6 Unit E — the offline restore is the OTHER way to become authenticated, and it never
    // passes through `applyToken`, so the device identity must be stamped here too or a restored
    // session would scope its local reads against a stale/absent owner and see nothing.
    void persistOwnerIdentity(offlineUser.userId);
    setToken(null);
    setUser(offlineUser);
    setStatus("authenticated");
    void triggerMainThreadDrain().catch(() => {});
  }, []);

  // Franklin#867 — peer tab signed out on the shared device: drop this tab's in-memory session
  // so clinician B never inherits A's live SPA state. Marker/IDB were written by the peer; we
  // only clear token + offline identity here (drafts/queue/owner_identity stay — AC18/AC22).
  React.useEffect(() => {
    return subscribeDeviceSignedOut((signedOut) => {
      if (!signedOut) return;
      setAccessToken(null);
      void clearOfflineIdentity();
    });
  }, []);

  React.useEffect(() => {
    // Keep context in sync whenever the api-client's token changes (e.g. a silent
    // refresh triggered by a 401 elsewhere).
    const unsubscribe = subscribeAccessToken(applyToken);
    // When the session can no longer be refreshed (a GENUINE expiry), drop to unauthenticated AND
    // clear the persisted offline identity — the route guards perform the redirect to /sign-in.
    // This is a reason-gated clear (gh-461): only a real expiry / sign-out clears the identity, so a
    // transport blip (now a non-terminal NETWORK_ERROR, never reaching onAuthExpired) cannot.
    setAuthExpiredHandler(() => {
      applyToken(null);
      void clearOfflineIdentity();
    });

    // Boot: the access token is memory-only and lost on reload, so attempt exactly ONE silent
    // refresh. gh-461 splits the outcome three ways:
    //   ok      → the subscription above already promoted + re-stamped the session; nothing to do.
    //   offline → the fetch never reached the server. Restore a credential-free offline-limited
    //             session, but ONLY when all three gates hold: a persisted identity exists, it is
    //             within the offline-grace window, AND there is pending offline work to protect
    //             (a non-empty outbound_queue). Otherwise fall through to /sign-in.
    //   expired → a genuine `!res.ok` refusal → sign-in + clear the (now-invalid) identity.
    let cancelled = false;
    void (async () => {
      // Franklin#867 Part A — hydrate the signed-out marker BEFORE any silent refresh. A live
      // refresh cookie after offline sign-out must not re-authenticate. Identity read is still
      // concurrent with the marker (both IDB, no network); refresh waits on the marker result.
      const [persisted, signedOut] = await Promise.all([
        readOfflineIdentity(),
        isDeviceSignedOut(),
      ]);
      if (cancelled) return;

      if (signedOut) {
        // Explicit sign-out on this device: stay on /sign-in. Do not refresh, do not offline-restore
        // (auth_session is already cleared on sign-out; refusing restore is belt-and-suspenders).
        applyToken(null);
        return;
      }

      // Silent refresh only when the device was not explicitly signed out. Concurrent with nothing
      // further — the marker gate above is the only sequencing #867 adds vs PR #468's parallel boot.
      const r = await refreshAccessToken();
      if (cancelled) return;

      if (r.status === "ok") {
        // Subscription promoted + stamped — nothing to do here.
        return;
      }

      if (r.status === "offline") {
        const now = Date.now();
        if (
          persisted &&
          isWithinGrace(persisted.lastValidatedAt, now) &&
          (await hasPendingOfflineWork(persisted.user.userId))
        ) {
          if (!cancelled) restoreOfflineIdentity(persisted.user);
        } else {
          if (!cancelled) applyToken(null);
          // Clear the identity ONLY when it is stale (past grace). A fresh identity with an empty
          // queue is kept: a later queued edit + reload can then restore. Never clear on a mere
          // transport failure with a still-fresh identity. gh-461 review (Minor 1): guard on
          // `!cancelled` to match the `applyToken` above — a late clear after unmount (HMR /
          // StrictMode double-invoke) must not wipe an identity a fresh mount just re-persisted; the
          // next boot re-detects staleness and clears anyway.
          if (!cancelled && persisted && !isWithinGrace(persisted.lastValidatedAt, now)) {
            await clearOfflineIdentity();
          }
        }
        return;
      }

      // r.status === "expired" — genuine session death. Guard on `!cancelled` to match every other
      // state mutation in this IIFE: a late resolve after unmount (HMR / StrictMode double-invoke)
      // must not act on a stale mount (PR #468 review, Minor).
      if (!cancelled) {
        applyToken(null);
        await clearOfflineIdentity();
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
      setAuthExpiredHandler(null);
    };
  }, [applyToken, restoreOfflineIdentity]);

  // gh-461 — reconnect promotion. Active ONLY while offline-restored (authenticated with a null
  // token). An `online` or `visibilitychange`→visible event re-validates with the server:
  //   ok      → the subscription promotes to a full session; drain the queued offline work.
  //   expired → the refresh family was revoked/expired → clear + sign-in.
  //   offline → still down; stay offline-limited and wait for the next event.
  // Deregisters on cleanup and, once promoted (accessToken non-null), the guard short-circuits.
  React.useEffect(() => {
    if (!(status === "authenticated" && accessToken === null)) return;
    let cancelled = false;
    const attempt = () => {
      void refreshAccessToken().then((r) => {
        if (cancelled) return;
        if (r.status === "ok") {
          void triggerMainThreadDrain().catch(() => {});
        } else if (r.status === "expired") {
          applyToken(null);
          void clearOfflineIdentity();
        }
        // offline → stay; a later event retries.
      });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") attempt();
    };
    window.addEventListener("online", attempt);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener("online", attempt);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status, accessToken, applyToken]);

  const setSession = React.useCallback(async (token: string) => {
    // Franklin#867 Part A — clear the durable marker BEFORE adopting the token. Latch flips
    // synchronously at the start of clearDeviceSignedOut so a racing refresh cannot resurrect the
    // prior clinician during the IDB round-trip; awaiting the delete means a full reload right after
    // login hydrates signed_out=false rather than refusing the new session's silent refresh.
    await clearDeviceSignedOut();
    setAccessToken(token);
  }, []);

  const signOut = React.useCallback(async () => {
    try {
      // body: {} — a bodyless POST 422'd before any revoke ran (Franklin#308).
      // skipAuthRefresh: a 401 here is "already logged out"; without it apiFetch
      // would silently re-mint a token from the live cookie mid-sign-out.
      await apiFetch("/auth/logout", { method: "POST", body: {}, skipAuthRefresh: true });
    } catch (err) {
      // Best-effort: even if the revoke call fails, we still clear the local
      // session below. But do NOT swallow silently — a failed server-side
      // revoke is exactly the regression that let R-02 slip through, so it
      // must leave a trace. (No Sentry/logger in this app yet — console.error.)
      console.error("Sign-out: server-side session revoke failed; clearing local session anyway.", err);
    }
    // Franklin#867 Part A — durable marker MUST land in IDB before we drop the session. A reload
    // that races an in-flight mark would hydrate signed_out=false and silent-refresh the previous
    // clinician back in (the hole this Part closes). Latch is still set synchronously first.
    // Does NOT clear drafts, outbound_queue, or owner_identity (AC18/AC22 / #461).
    await markDeviceSignedOut();
    // Drop the token; the subscription routes this through applyToken(null), the single
    // unauthenticated chokepoint that wipes the TanStack Query cache (Franklin#365) — so
    // the next clinician in this same tab cannot be served the prior session's cached PHI.
    setAccessToken(null); // → subscription → applyToken(null) → clear cache + unauthenticated
    // gh-461 — explicit sign-out is a genuine session end: drop the persisted offline identity too,
    // so a subsequent offline reload cannot restore this clinician's shell.
    void clearOfflineIdentity();
  }, []);

  const hasRole = React.useCallback(
    (...roles: Array<Role | string>) => Boolean(user && roles.includes(user.role)),
    [user],
  );

  const isLegacyGated = deriveIsLegacyGated(user);

  const value = React.useMemo<AuthContextValue>(
    () => ({ status, user, accessToken, setSession, signOut, hasRole, isLegacyGated }),
    [status, user, accessToken, setSession, signOut, hasRole, isLegacyGated],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
